import { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import {
  Building2, CalendarCheck, CalendarDays, Sparkles, Wrench, CreditCard, BarChart3, Users, KeyRound,
  Settings, ShieldCheck, LogOut, Menu, X, Languages, Receipt, Calculator, Gauge, History, FlaskConical,
  Rocket, QrCode, PiggyBank, Map, Briefcase, FileSpreadsheet, MessageSquare, GraduationCap, Landmark,
  ClipboardCheck, TrendingUp, Compass, HeartHandshake, Wallet, ChevronDown, Sun,
} from "lucide-react";
import HelpButton from "./HelpButton";
import { Button } from "@/components/ui/button";
import { LOGO_URL } from "../brand";

// Voce singola "Oggi" (Centro Operativo) — sempre visibile a tutti
const HOME_ITEM = { to: "/", key: "homeOggi", section: null, icon: Sun, end: true };

// Gruppi collassabili → mappano SOLO pagine esistenti
const NAV_GROUPS = [
  { key: "operazioni", label: "Operazioni", icon: Compass, items: [
    { to: "/dashboard", key: "dashboard", section: "dashboard", icon: Gauge },
    { to: "/bookings", key: "bookings", section: "bookings", icon: CalendarCheck },
    { to: "/calendar", key: "calendar", section: "calendar", icon: CalendarDays },
    { to: "/apartments", key: "apartments", section: "apartments", icon: Building2 },
    { to: "/pipeline", key: "pipeline", section: "pipeline", icon: Briefcase },
  ]},
  { key: "ospiti", label: "Ospiti & Customer Care", icon: HeartHandshake, items: [
    { to: "/communication", key: "communicationCenter", section: "communication", icon: MessageSquare },
    { to: "/guests", key: "guestsSection", section: "guests", icon: Users },
    { to: "/qr-center", key: "qrCenter", section: "qr", icon: QrCode },
    { to: "/local-guide", key: "localGuide", section: "qr", icon: Map },
  ]},
  { key: "pulizie", label: "Pulizie & Manutenzioni", icon: Sparkles, items: [
    { to: "/cleanings", key: "cleanings", section: "cleanings", icon: Sparkles },
    { to: "/maintenance", key: "maintenance", section: "maintenance", icon: Wrench },
  ]},
  { key: "finanza", label: "Amministrazione & Finanza", icon: Wallet, items: [
    { to: "/payments", key: "payments", section: "payments", icon: CreditCard },
    { to: "/owners", key: "owners", section: "owners", icon: KeyRound },
    { to: "/owner-portal", key: "ownerPortalPro", section: "owner_portal", icon: PiggyBank },
    { to: "/statements", key: "statements", section: "statements", icon: Receipt },
    { to: "/accounting", key: "accounting", section: "accounting", icon: Calculator },
    { to: "/report-center", key: "reportCenter", section: "finance", icon: FileSpreadsheet },
    { to: "/financial-engine", key: "financialEngine", section: "contracts", icon: FlaskConical },
    { to: "/city-tax", key: "cityTax", section: "city_tax", icon: Landmark },
    { to: "/reports", key: "reports", section: "reports", icon: BarChart3 },
  ]},
  { key: "revenue", label: "Revenue", icon: TrendingUp, items: [
    { to: "/revenue", key: "revenueEngine", section: "revenue", icon: TrendingUp },
  ]},
  { key: "config", label: "Configurazione", icon: Settings, items: [
    { to: "/users", key: "usersPermissions", section: "users", icon: ShieldCheck },
    { to: "/settings", key: "settings", section: "settings", icon: Settings },
    { to: "/academy", key: "academy", section: "academy", icon: GraduationCap },
    { to: "/audit", key: "auditLog", section: "audit", icon: History },
    { to: "/release", key: "releaseDashboard", section: "release", icon: Rocket },
  ]},
];

const FLAT_ITEMS = [HOME_ITEM, ...NAV_GROUPS.flatMap((g) => g.items)];
const LS_KEY = "urbanos_nav_open_groups";

function itemLabel(it, t, sections) {
  if (it.key === "homeOggi") return "Oggi";
  if (it.key === "dashboard" && sections.includes("executive")) return t("homeDirezionale");
  return t(it.key);
}

function ChildLink({ it, onNav, t, sections }) {
  const Icon = it.icon;
  return (
    <NavLink to={it.to} end={it.end} onClick={onNav} data-testid={`nav-${it.key}`}
      className={({ isActive }) =>
        `group/link relative flex items-center gap-2.5 rounded-md py-2 pl-9 pr-3 text-sm font-medium transition-colors duration-150 ${
          isActive ? "bg-[#EAE7DF]/50 text-[#0A1629]" : "text-slate-500 hover:bg-[#EAE7DF]/40 hover:text-slate-800"
        }`}>
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-3 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full bg-[#C5A059]" />}
          <Icon size={16} strokeWidth={1.5} />
          <span className="truncate">{itemLabel(it, t, sections)}</span>
        </>
      )}
    </NavLink>
  );
}

function NavGroup({ group, sections, open, onToggle, onNav, t }) {
  const visible = group.items.filter((it) => sections.includes(it.section));
  if (visible.length === 0) return null;
  const Icon = group.icon;
  return (
    <div className="mb-0.5">
      <button type="button" onClick={() => onToggle(group.key)} data-testid={`nav-group-${group.key}`}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-[13px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-[#EAE7DF]/50">
        <Icon size={17} strokeWidth={1.5} className="text-slate-500" />
        <span className="flex-1 truncate">{group.label}</span>
        <ChevronDown size={15} className={`text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div aria-hidden={!open} className={`overflow-hidden transition-[max-height] duration-200 ease-in-out ${open ? "max-h-[600px]" : "max-h-0 pointer-events-none"}`}>
        <div className="mt-0.5 flex flex-col gap-0.5 border-l border-[#EAE7DF] pl-1.5 ml-4">
          {visible.map((it) => <ChildLink key={it.to} it={it} onNav={onNav} t={t} sections={sections} />)}
        </div>
      </div>
    </div>
  );
}

function NavContent({ onNav }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const location = useLocation();
  const sections = user?.sections || [];

  const activeGroup = NAV_GROUPS.find((g) => g.items.some((it) =>
    location.pathname === it.to || (it.to !== "/" && location.pathname.startsWith(it.to + "/"))))?.key;

  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
  });
  useEffect(() => {
    if (activeGroup && !openGroups[activeGroup]) {
      setOpenGroups((p) => ({ ...p, [activeGroup]: true }));
    }
  }, [activeGroup]); // eslint-disable-line
  const toggle = useCallback((k) => {
    setOpenGroups((p) => { const n = { ...p, [k]: !p[k] }; localStorage.setItem(LS_KEY, JSON.stringify(n)); return n; });
  }, []);

  const homeVisible = HOME_ITEM.section == null || sections.includes(HOME_ITEM.section);
  return (
    <nav className="flex flex-col gap-0.5 px-3">
      {homeVisible && (
        <NavLink to={HOME_ITEM.to} onClick={onNav} data-testid="nav-homeOggi"
          className={({ isActive }) =>
            `mb-1 flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors duration-150 ${
              isActive ? "bg-[#0A1629] text-white" : "bg-[#F7F2EA] text-[#0A1629] hover:bg-[#E8D8B0]/60"
            }`}>
          <Sun size={18} strokeWidth={1.8} /> Oggi
        </NavLink>
      )}
      {NAV_GROUPS.map((g) => (
        <NavGroup key={g.key} group={g} sections={sections} open={!!openGroups[g.key]}
          onToggle={toggle} onNav={onNav} t={t} />
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="px-5 py-5">
      <div className="flex items-center justify-center rounded-xl bg-[#F7F2EA] p-3">
        <img src={LOGO_URL} alt="Urban Dreams Home" className="max-h-24 w-auto object-contain" />
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { t, lang, setLang } = useI18n();
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const sections = user?.sections || [];
  const current = FLAT_ITEMS.find((n) => n.end ? location.pathname === n.to : (location.pathname === n.to || location.pathname.startsWith(n.to + "/")));
  const title = current ? itemLabel(current, t, sections) : "Urban OS";

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex">
        <Brand />
        <div className="mt-1 flex-1 overflow-y-auto pb-4"><NavContent /></div>
        <div className="border-t border-slate-200 p-3">
          <div className="mb-1 px-3 text-xs font-medium text-slate-700 truncate">{user?.name || user?.email}</div>
          <div className="mb-2 px-3 text-[11px] text-slate-400 truncate" data-testid="sidebar-role">{user?.role ? t(`role_${user.role}`) : ""}</div>
          <Button data-testid="logout-btn" variant="ghost" onClick={logout} className="w-full justify-start gap-3 text-slate-600 hover:text-slate-900">
            <LogOut size={18} /> {t("logout")}
          </Button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 flex w-72 flex-col bg-white shadow-xl fade-in">
            <div className="flex items-center justify-between">
              <Brand />
              <Button data-testid="close-menu-btn" variant="ghost" size="icon" onClick={() => setOpen(false)} className="mr-3"><X size={20} /></Button>
            </div>
            <div className="mt-1 flex-1 overflow-y-auto pb-4"><NavContent onNav={() => setOpen(false)} /></div>
            <div className="border-t border-slate-200 p-3">
              <Button data-testid="logout-btn-mobile" variant="ghost" onClick={logout} className="w-full justify-start gap-3 text-slate-600">
                <LogOut size={18} /> {t("logout")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/70 px-4 py-3 backdrop-blur-xl sm:px-8">
          <div className="flex items-center gap-3">
            <Button data-testid="open-menu-btn" variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)}><Menu size={22} /></Button>
            <h1 className="font-head text-xl font-semibold text-slate-900 sm:text-2xl">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <HelpButton />
            <Button data-testid="lang-toggle" variant="outline" size="sm" className="gap-2" onClick={() => setLang(lang === "it" ? "en" : "it")}>
              <Languages size={16} /> {lang.toUpperCase()}
            </Button>
          </div>
        </header>
        <main className="p-4 sm:p-8">{children}</main>
      </div>
    </div>
  );
}
